import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";

import { useSelector } from "react-redux";
import { selectCurrentProduct } from "../../productSlice";

import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";

import { CustomScrollbar } from "../customScrollbar/CustomScrollbar";

import { Icon } from "@mui/material";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const ContainedIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },

  position: "fixed",
  top: "125px",
  ...(open && {
    left: `220px`,
    transition: theme.transitions.create("left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    left: `calc(${theme.spacing(8)} - 15px)`,
    transition: theme.transitions.create("left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  }),
}));

const CustomListItemButton = styled(ListItemButton)`
  border-radius: 10px;
  &.Mui-selected {
    color: white;
    background-color: #0c3759;
    :hover {
      background-color: #0c4070;
    }
  }
`;

const CustomListItemIcon = styled(ListItemIcon)(({ selected }) => ({
  color: !selected ? "#0c3759" : "white",
  minWidth: 0,
  justifyContent: "center",
}));

export default function MiniDrawer({
  children,
  items,
  handleChange,
  firstItemKey,
}) {
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(firstItemKey);
  const currentProduct = useSelector(selectCurrentProduct);

  useEffect(() => {
    if (currentProduct) {
      setSelectedItem(currentProduct.api_key);
    }
  }, [currentProduct]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleListItemClick = (item) => {
    console.log("🚀 ~ handleListItemClick ~ item:", item);
    setSelectedItem(item);
    handleChange(item);
  };

  return (
    <Box display="flex">
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          [`& .MuiDrawer-paper`]: { boxSizing: "border-box" },
        }}
      >
        <DrawerHeader />
        <CustomScrollbar>
          <List>
            <Box display="flex" minWidth={20}>
              <ListItem key={"firstElement"} sx={{ display: "block" }}>
                <CustomListItemButton
                  selected={selectedItem === firstItemKey}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                  onClick={() => handleListItemClick(firstItemKey)}
                >
                  <CustomListItemIcon
                    sx={{ mr: open ? 3 : "auto" }}
                    selected={selectedItem === firstItemKey}
                  >
                    <InboxIcon />
                  </CustomListItemIcon>
                  {open && <ListItemText primary="Dashboard" />}
                </CustomListItemButton>
              </ListItem>
              <ContainedIconButton
                size="small"
                open={open}
                onClick={open ? handleDrawerClose : handleDrawerOpen}
              >
                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </ContainedIconButton>
            </Box>
            {items.map((product) => (
              <ListItem key={product.id} sx={{ display: "block" }}>
                <CustomListItemButton
                  selected={selectedItem === product.api_key}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                  onClick={() => handleListItemClick(product.api_key)}
                >
                  <Icon
                    sx={{ mr: open ? 3 : "auto" }}
                    selected={selectedItem === product.api_key}
                  >
                    {product.icon}
                  </Icon>
                  {open && <ListItemText primary={product.title} />}
                </CustomListItemButton>
              </ListItem>
            ))}
          </List>
        </CustomScrollbar>
      </Drawer>
      <Box p={2} width="100%">
        {children}
      </Box>
    </Box>
  );
}
